<template>
  <header :class="['header', { 'header--hidden': !isVisible, 'header--scrolled': isScrolled }]">
    <div class="header__container">
      <nav>
        <ul class="header__links">
          <li class="header__item">
            <BaseLink :href="logoHref">
              <div class="logo">
                <div class="logo-circle">
                  <BasePicture
                    class="header__logo"
                    src="/logos/logo-black-new.svg"
                  />
                </div>
                MyDonut
              </div>
            </BaseLink>
          </li>

          <li
            v-for="(link, linkIndex) in MENU"
            :key="linkIndex"
            class="header__item"
          >
            <BaseLink
              class="header__link"
              :href="link.href"
            >
              <span v-html="link.name" />
            </BaseLink>
          </li>
        </ul>
      </nav>

      <div class="header__tools-container">
        <TheHeaderTools />

        <BaseButton2
          class="header__burger"
          @click="() => openMenu()"
        >
          <BaseIcon2
            id="icon-burger"
            height="24"
            width="24"
          />
        </BaseButton2>
      </div>
    </div>

    <TheMenuBeautyNailMobile
      class="ui-scrollbar-primary"
      :menu="MENU"
    />
  </header>
</template>

<script setup lang="ts">
const isVisible = ref(true);
const isScrolled = ref(false);
let lastScrollPosition = 0;

const route = useRoute();

const logoHref = computed(() => {
  const path = route.path;
  if (path.endsWith("/beauty") || path.endsWith("/beauty/")) {
    return path;
  }
  if (path.endsWith("/ecommerce") || path.endsWith("/ecommerce/")) {
    return path;
  }
  return "/";
});

const MENU = computed(() => {
  const isMainPage =
    route.path === "/" ||
    route.path.endsWith("/ru") ||
    route.path.endsWith("/ru/") ||
    route.path.endsWith("/en") ||
    route.path.endsWith("/en/") ||
    route.path.endsWith("/ka") ||
    route.path.endsWith("/ka/") ||
    route.path.endsWith("/uz") ||
    route.path.endsWith("/uz/") ||
    route.name?.toString().startsWith("index");

  if (isMainPage) {
    return [
      {
        href: "#directions",
        name: "Возможности",
      },
      {
        href: "#examples",
        name: "Кейсы",
      },
      {
        href: "/rates",
        name: "Тарифы",
      },
      {
        href: "#comparison",
        name: "Сравнение",
      },
    ];
  }

  return [
    {
      href: "#why-donut",
      name: "Возможности",
    },
    {
      href: "#testimonials",
      name: "Кейсы",
    },
    {
      href: "#pricing",
      name: "Цены",
    },
    {
      href: "#comparison",
      name: "Сравнение",
    },
  ];
});

const { toggleMenu } = useAppStore();

const openMenu = () => {
  toggleMenu(true);
  pageScrollBlock();
};

const handleScroll = () => {
  const currentScrollPosition = window.pageYOffset || document.documentElement.scrollTop;

  if (currentScrollPosition < 0) {
    return;
  }

  isScrolled.value = currentScrollPosition > 50;

  if (currentScrollPosition <= 50) {
    isVisible.value = true;
  } else if (currentScrollPosition < lastScrollPosition) {
    isVisible.value = true;
  } else {
    isVisible.value = false;
  }

  lastScrollPosition = currentScrollPosition;
};

onMounted(() => {
  window.addEventListener("scroll", handleScroll);
  handleScroll();
});

onUnmounted(() => {
  window.removeEventListener("scroll", handleScroll);
});
</script>

<style scoped>
.header {
  position: sticky;
  top: 0;
  z-index: 100;
  padding-top: 16px;
  transition:
    transform 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    padding-top 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.header--hidden {
  transform: translateY(-100%);
}

.header--scrolled {
  padding-top: 0;
}

.header__container {
  position: relative;
  z-index: var(--z-index-header);
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1286px;
  height: 48px;
  margin: 0 auto;
  padding: 4px 12px 4px 24px;
  border-radius: 50px;
  background-color: var(--white);
  box-shadow: 0 3px 16px 0 rgb(0 0 0 / 8%);
}

@media (--desktop) {
  .header__container {
    min-width: 1036px;
    height: 68px;
    padding: 0 40px;
  }

  .header__container.active {
    border-radius: 34px 34px 0 0;
  }
}

.header__links,
.header__item {
  display: flex;
  align-items: center;
}

.header__link {
  display: none;
}

@media (--desktop) {
  .header__link {
    display: flex;
    align-items: center;
    column-gap: 4px;
    padding: 8px 24px;
  }

  .header__link.active {
    color: var(--orange-3);
  }
}

@media (--hover) {
  .header__link:hover,
  .header__link:active {
    color: var(--orange-3);
  }
}

.header__tools-container {
  display: flex;
  align-items: center;
  column-gap: 12px;
}

.header__burger {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  color: var(--grey-3);
}

@media (--desktop) {
  .header__burger {
    display: none;
  }
}

.logo {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  font-size: 16px;
  font-family:
    "Plus Jakarta Sans",
    "Inter",
    system-ui,
    -apple-system,
    sans-serif;
}

@media (--desktop) {
  .logo {
    gap: 12px;
    font-size: 20px;
  }
}

.logo-circle {
  height: 25px;
  width: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (--desktop) {
  .logo-circle {
    width: 40px;
    height: 40px;
  }
}
</style>
